/* eslint-disable i18next/no-literal-string */
import React, { useState } from 'react'
import { func, string, bool } from 'prop-types'
import classNames from 'classnames'
import { renderItemType } from 'models/propTypes'
import { convertMeterDimensionsByLocale, convertDimensionsToMeterByLocale } from 'utils/convertDimensions'
import { LOCALES_USING_IMPERIAL_SYSTEM } from 'models/location/constants'
import Dropdown from 'components/formElements/Dropdown'
import { Input } from 'components/FormElements'
import { ART_FRAMES, ART_SIZES } from 'models/furniture/artConstants'

require('./OptionSelectorView.scss')

const ACTUAL_SIZE_ID = 1
const CUSTOM_SIZE_ID = 999

const OptionSelectorView = ({ selectedRenderItem, updateRenderItem, locale, canEditArtSize }) => {
    const artRatio = selectedRenderItem.displayWidthCm / selectedRenderItem.displayHeightCm
    const { height: initialHeight } = selectedRenderItem.size
    const [customHeight, setCustomHeight] = useState(convertMeterDimensionsByLocale(initialHeight, locale))
    const [customWidth, setCustomWidth] = useState(convertMeterDimensionsByLocale(initialHeight * artRatio, locale))

    const updateArtItem = (event, data) => {
        if (event) {
            event.stopPropagation()
        }
        updateRenderItem(data)
    }

    const updateSize = (e) => {
        const { name, value } = e.target
        let height = null
        if (value === CUSTOM_SIZE_ID) {
            height = selectedRenderItem.size.height

            if (value !== selectedRenderItem.sizeId) {
                setCustomHeight(convertMeterDimensionsByLocale(height, locale))
                setCustomWidth(convertMeterDimensionsByLocale(height * artRatio, locale))
            }
        }
        updateArtItem(e, {
            [name]: value,
            customHeight: height,
        })
    }

    const updateCustomSize = (e) => {
        const { name, value } = e.target
        let height
        let width

        if (name === 'height') {
            height = value
            width = value * artRatio
        } else {
            width = value
            height = value / artRatio
        }
        setCustomHeight(Math.ceil(height * 4) / 4)
        setCustomWidth(Math.ceil(width * 4) / 4)

        updateArtItem(e, { customHeight: convertDimensionsToMeterByLocale(height, locale) })
    }

    const makeLabel = ({ id, name, height: sizeHeight }) => {
        if (id === CUSTOM_SIZE_ID) {
            return name
        }
        let height = sizeHeight
        let suffix = ''
        if (id === ACTUAL_SIZE_ID) {
            height = selectedRenderItem.displayHeightCm / 100
            suffix = ' (actual)'
        }
        const dimensionSymbol = LOCALES_USING_IMPERIAL_SYSTEM.includes(locale) ? '"' : 'cm'
        return `${convertMeterDimensionsByLocale(
            height * artRatio,
            locale,
        )}${dimensionSymbol} x ${convertMeterDimensionsByLocale(height, locale)}${dimensionSymbol}${suffix}`
    }

    if (!selectedRenderItem) {
        return <div className="OptionSelectorView invalid">Select something in your room</div>
    }

    const {
        frame: { color, colors, mattes, matte, name: frameName },
        frameId: selectedFrameId,
        colorId: selectedColorId,
        matteId: selectedMatteId,
        sizeId: selectedSizeId,
    } = selectedRenderItem

    return (
        <div className="OptionSelectorView">
            <div className="selectorsContainer">
                <div className="selectors">
                    {canEditArtSize && (
                        <div className="selectorRow size">
                            <div className="label">
                                <div className="title">Size</div>
                            </div>
                            <div className="options">
                                <Dropdown
                                    name="sizeId"
                                    value={selectedSizeId}
                                    options={Object.values(ART_SIZES).map((item) => ({
                                        key: item.id,
                                        label: makeLabel(item),
                                    }))}
                                    onChange={updateSize}
                                />
                                {selectedSizeId === CUSTOM_SIZE_ID ? (
                                    <div className="customSize">
                                        <div className="sizeOption">
                                            <span>Width</span>
                                            <Input
                                                type="number"
                                                name="width"
                                                onChange={updateCustomSize}
                                                value={customWidth}
                                                step={0.25}
                                            />
                                        </div>
                                        <div className="sizeOption">
                                            <span>Height</span>
                                            <Input
                                                type="number"
                                                name="height"
                                                onChange={updateCustomSize}
                                                value={customHeight}
                                                step={0.25}
                                            />
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    )}
                    <div className="selectorRow">
                        <div className="label">
                            <div className="title">Frames</div>
                            <div className="description">
                                {frameName}
                                {selectedFrameId !== 1 ? ' (Frame not included)' : ''}
                            </div>
                        </div>
                        <div className="options">
                            {Object.keys(ART_FRAMES).map((id) => {
                                const frameId = parseInt(id, 10)
                                const frameData = ART_FRAMES[frameId]
                                const selected = frameId === selectedFrameId

                                return (
                                    <div
                                        className={classNames('frameOption', {
                                            selected,
                                        })}
                                        key={frameId}
                                    >
                                        <div className="thumb">
                                            <div
                                                className="pic"
                                                style={{ backgroundImage: `url('${frameData.thumbUrl}')` }}
                                                onMouseDown={(e) => {
                                                    updateArtItem(e, { frameId })
                                                }}
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                    {color && (
                        <div className="selectorRow">
                            <div className="label">
                                <div className="title">Colors</div>
                                <div className="description">{color.name}</div>
                            </div>
                            <div className="options">
                                {colors &&
                                    Object.keys(colors).map((cId) => {
                                        const colorId = parseInt(cId, 10)
                                        const colorData = colors[colorId]
                                        const selected = selectedColorId === colorId
                                        return (
                                            <div
                                                className={classNames('colorOption', {
                                                    selected,
                                                })}
                                                key={colorId}
                                            >
                                                <div className="color">
                                                    <div
                                                        className="colorInner"
                                                        style={{ backgroundColor: colorData.hex }}
                                                        onMouseDown={(e) => {
                                                            updateArtItem(e, { colorId })
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    })}
                            </div>
                        </div>
                    )}
                    <div className="selectorRow">
                        <div className="label">
                            <div className="title">Mats</div>
                            <div className="description">{matte.name}</div>
                        </div>
                        <div className="options">
                            {Object.keys(mattes).map((mId) => {
                                const matteId = parseInt(mId, 10)
                                const matteData = mattes[matteId]
                                const selected = matteId === selectedMatteId
                                return (
                                    <div className={classNames('matteOption', matteData.key)} key={matteData.key}>
                                        <div
                                            className={classNames('thumb', { selected })}
                                            onMouseDown={(e) => updateArtItem(e, { matteId })}
                                        >
                                            <div className="innerThumb">
                                                <div className="matteBorder" style={{ backgroundColor: matteData.hex }}>
                                                    <div className="innerArt" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

OptionSelectorView.propTypes = {
    selectedRenderItem: renderItemType.isRequired,
    updateRenderItem: func.isRequired,
    locale: string.isRequired,
    canEditArtSize: bool.isRequired,
}

export default OptionSelectorView
